import { motion, useReducedMotion } from "framer-motion";
import * as React from "react";
import { buttonVariants, type ButtonProps } from "./button";
import { cn } from "./utils";

type Direction = "TOP" | "LEFT" | "BOTTOM" | "RIGHT";

const DIRECTIONS: Direction[] = ["TOP", "LEFT", "BOTTOM", "RIGHT"];

/** A soft light that sits on one edge of the border and moves round it. */
const BORDER_LIGHT: Record<Direction, string> = {
    TOP: "radial-gradient(20.7% 50% at 50% 0%, #a855f7 0%, rgba(168, 85, 247, 0) 100%)",
    LEFT: "radial-gradient(16.6% 43.1% at 0% 50%, #a855f7 0%, rgba(168, 85, 247, 0) 100%)",
    BOTTOM: "radial-gradient(20.7% 50% at 50% 100%, #a855f7 0%, rgba(168, 85, 247, 0) 100%)",
    RIGHT: "radial-gradient(16.2% 41.2% at 100% 50%, #a855f7 0%, rgba(168, 85, 247, 0) 100%)",
};

/** On hover the light floods the whole border. */
const HOVER_LIGHT = "radial-gradient(75% 181.16% at 50% 50%, #c026d3 0%, rgba(192, 38, 211, 0) 100%)";

export interface AiButtonProps extends Omit<ButtonProps, "variant" | "asChild" | "tooltip" | "tooltipSide"> {
    /** Seconds for the light to move from one edge to the next. */
    duration?: number;
    clockwise?: boolean;
}

/**
 * The button for AI features. It looks like an outline Button, so it never takes over the page,
 * but a violet light travels round its border and fills the border on hover.
 * Shares sizes, focus ring and disabled styles with Button.
 */
const AiButton = React.forwardRef<HTMLButtonElement, AiButtonProps>(
    (
        { className, size, duration = 1, clockwise = true, children, onMouseEnter, onMouseLeave, type, ...props },
        ref
    ) => {
        const reduceMotion = useReducedMotion();
        const [hovered, setHovered] = React.useState(false);
        const [direction, setDirection] = React.useState<Direction>("TOP");

        React.useEffect(() => {
            if (hovered || reduceMotion) return;
            const interval = setInterval(() => {
                setDirection((current) => {
                    const index = DIRECTIONS.indexOf(current);
                    const step = clockwise ? -1 : 1;
                    return DIRECTIONS[(index + step + DIRECTIONS.length) % DIRECTIONS.length] ?? current;
                });
            }, duration * 1000);
            return () => clearInterval(interval);
        }, [hovered, reduceMotion, duration, clockwise]);

        return (
            <button
                ref={ref}
                type={type ?? "button"}
                className={cn(
                    buttonVariants({ variant: null, size }),
                    "relative isolate overflow-hidden bg-violet-200 text-slate-800 dark:bg-violet-900 dark:text-slate-100",
                    "[&_svg]:text-violet-600 dark:[&_svg]:text-violet-300",
                    "focus-visible:ring-violet-500 dark:focus-visible:ring-violet-400",
                    className
                )}
                onMouseEnter={(event) => {
                    setHovered(true);
                    onMouseEnter?.(event);
                }}
                onMouseLeave={(event) => {
                    setHovered(false);
                    onMouseLeave?.(event);
                }}
                {...props}
            >
                <motion.span
                    aria-hidden
                    className="absolute inset-0 -z-20 rounded-[inherit]"
                    style={{ filter: "blur(2px)" }}
                    initial={{ background: BORDER_LIGHT[direction] }}
                    animate={{
                        background: hovered ? [BORDER_LIGHT[direction], HOVER_LIGHT] : BORDER_LIGHT[direction],
                    }}
                    transition={{ ease: "linear", duration: reduceMotion ? 0 : duration }}
                />
                <span
                    aria-hidden
                    className="absolute inset-[1.5px] -z-10 rounded-[inherit] bg-slate-50 transition-colors duration-300 group-hover:bg-white dark:bg-slate-900 dark:group-hover:bg-slate-800"
                />
                <span className="inline-flex items-center gap-[inherit]">{children}</span>
            </button>
        );
    }
);
AiButton.displayName = "AiButton";

export { AiButton };
